import {
  type AskPunttAgentStep,
  type AskPunttContentBlock,
} from "@mg/schemas/src/christo/askPuntt";

import { toRenderableSegmentContent } from "./messageSegments";

import { prefixOfHeldText } from "../../../ask-v2/transport/fitCut";

export type DisplaySegment =
  | { type: "text"; content: string }
  | { type: "tools"; steps: AskPunttAgentStep[] };

/**
 * Strip known step contents from the full content string to extract only
 * trailing text not covered by any agentStep. The backend joins rounds
 * with "\n\n", so we peel off known prefixes in order.
 */
function computeFinalContent(
  fullContent: string,
  knownContents: string[],
): string {
  if (!knownContents.length) return fullContent;

  let remaining = fullContent;
  let stripped = 0;
  for (const known of knownContents) {
    if (remaining.startsWith(known)) {
      remaining = remaining.slice(known.length);
      if (remaining.startsWith("\n\n")) {
        remaining = remaining.slice(2);
      }
      stripped++;
    }
  }
  if (stripped === knownContents.length) return remaining.trim();

  remaining = fullContent;
  stripped = 0;
  for (const known of knownContents) {
    const trimmedKnown = known.trim();
    const trimmedRemaining = remaining.trimStart();
    if (trimmedKnown && trimmedRemaining.startsWith(trimmedKnown)) {
      const idx = remaining.indexOf(trimmedKnown);
      remaining = remaining.slice(idx + trimmedKnown.length);
      if (remaining.startsWith("\n\n")) {
        remaining = remaining.slice(2);
      }
      stripped++;
    }
  }
  if (stripped === knownContents.length) return remaining.trim();

  return "";
}

function coalesceConsecutiveToolSegments(
  segments: DisplaySegment[],
): DisplaySegment[] {
  const out: DisplaySegment[] = [];
  for (const seg of segments) {
    if (seg.type === "tools" && seg.steps.length === 0) continue;
    const prev = out[out.length - 1];
    if (seg.type === "tools" && prev?.type === "tools") {
      out[out.length - 1] = {
        type: "tools",
        steps: [...prev.steps, ...seg.steps],
      };
      continue;
    }
    out.push(seg);
  }
  return out;
}

function mergeSegmentInPlace(
  previous: DisplaySegment,
  next: DisplaySegment,
  allowShrink: boolean,
): DisplaySegment {
  if (previous.type === "text" && next.type === "text") {
    // The transport's unstamped-cut shape test, shared rather than restated:
    // both sides decide whether a shorter value replaces a longer one the
    // person is already reading, so they must answer it the same way or the
    // thread shrinks on one path and holds on the other.
    //
    // This NARROWS the default hold on purpose. The rule used to be "hold the
    // longer text against anything shorter" (`next.length >= previous.length`),
    // which held every shorter frame, cut or not. Now the default holds only
    // the shape a size cut actually has — the text on screen with its tail
    // missing — so a shorter value that is NOT a prefix is taken, where before
    // it was refused. That shorter non-prefix frame is a REWRITE, not a cut
    // (warhol's reducer resetting its text on a new part, rembrandt re-deriving
    // the line without a leaked `att_N`, a settled row's shaped `content`), and
    // holding it is what froze stale text on screen until the new text happened
    // to outgrow it. Cuts are still refused: stamped ones a layer up in
    // `ask-v2/transport/applyStreamSnapshot.ts::fitCutThisFrame`, unstamped ones
    // by the prefix test below.
    const content =
      !allowShrink && prefixOfHeldText(next.content, previous.content)
        ? previous.content
        : next.content;
    return { type: "text", content };
  }
  if (previous.type === "tools" && next.type === "tools") {
    const steps =
      next.steps.length >= previous.steps.length ? next.steps : previous.steps;
    return { type: "tools", steps };
  }
  return previous;
}

/**
 * Candidate segment order for the current frame (Path-1/2/3). May reshuffle
 * when contentBlocks replace live agentSteps — callers must pass through
 * {@link stabilizeDisplaySegments} while a message is on screen.
 */
export type ComputeRawDisplaySegmentsOptions = {
  /**
   * While a turn is still streaming, ignore partial `contentBlocks` snapshots
   * from history polls — they can land before `agentSteps` is in sync and
   * produce empty tool_activity slices that make tool rows vanish mid-stream.
   */
  ignoreContentBlocks?: boolean;
};

export function computeRawDisplaySegments(
  agentSteps: AskPunttAgentStep[] | undefined,
  displayContent: string,
  contentBlocks?: AskPunttContentBlock[] | null,
  options?: ComputeRawDisplaySegmentsOptions,
): DisplaySegment[] {
  if (contentBlocks?.length && !options?.ignoreContentBlocks) {
    const segments = contentBlocks
      .map((block): DisplaySegment | null => {
        if (block.type === "text") {
          const content = toRenderableSegmentContent(block.content);
          return content == null ? null : { type: "text" as const, content };
        }
        if (block.type === "tool_activity") {
          const [start, end] = block.stepRange;
          return {
            type: "tools" as const,
            steps: (agentSteps ?? []).slice(start, end),
          };
        }
        return null;
      })
      .filter(Boolean) as DisplaySegment[];

    if (!segments.some((segment) => segment.type === "tools")) {
      const toolSteps = (agentSteps ?? []).filter(
        (step) => step.type !== "thinking" && step.type !== "text",
      );
      if (toolSteps.length) {
        const firstTextIdx = segments.findIndex(
          (segment) => segment.type === "text",
        );
        if (firstTextIdx >= 0) {
          return coalesceConsecutiveToolSegments([
            ...segments.slice(0, firstTextIdx + 1),
            { type: "tools" as const, steps: toolSteps },
            ...segments.slice(firstTextIdx + 1),
          ]);
        }
        return coalesceConsecutiveToolSegments([
          { type: "tools" as const, steps: toolSteps },
          ...segments,
        ]);
      }
    }

    return coalesceConsecutiveToolSegments(segments);
  }

  if (!agentSteps?.length) {
    const content = toRenderableSegmentContent(displayContent);
    if (content != null) return [{ type: "text", content }];
    return [];
  }

  const segments: DisplaySegment[] = [];
  let currentToolSteps: AskPunttAgentStep[] = [];
  const knownContents: string[] = [];

  for (const step of agentSteps) {
    if (step.type === "thinking" || step.type === "text") {
      if (currentToolSteps.length) {
        segments.push({ type: "tools", steps: [...currentToolSteps] });
        currentToolSteps = [];
      }
      if (step.content.trim()) {
        const content = toRenderableSegmentContent(step.content);
        if (content != null) {
          segments.push({ type: "text", content });
        }
        knownContents.push(step.content);
      }
    } else {
      currentToolSteps.push(step);
    }
  }

  if (currentToolSteps.length) {
    segments.push({ type: "tools", steps: [...currentToolSteps] });
  }

  if (displayContent) {
    const finalContent = computeFinalContent(displayContent, knownContents);
    const renderable = toRenderableSegmentContent(finalContent);
    if (renderable != null) {
      segments.push({ type: "text", content: renderable });
    }
  }

  return coalesceConsecutiveToolSegments(segments);
}

export type StabilizeDisplaySegmentsOptions = {
  /**
   * Let a text slot take a SHORTER value even when it reads as truncated.
   *
   * {@link prefixOfHeldText} guesses a frame's provenance from its shape, and
   * the shape is ambiguous: warhol's size fit cuts to a strict prefix, but so
   * do two frames this row is entitled to take. A LIVE row's own writer
   * re-derives its text shorter (rembrandt dropping a leaked `att_N`, or the
   * tick that first sees a complete ```canvas opener falling back to the prose
   * before the fence) — held, the leak or the fence fragment stays on screen
   * until the prose outgrows it. And a SETTLED row hands off to a shaped
   * `content` that is a prefix of the raw reply (the `place_markers`
   * source-note fold) — held, the settled answer never lands at all.
   *
   * So the caller says what it knows about the ROW and the shape test only
   * decides for the frames it was right about. The cut it IS right about is
   * refused a layer up, on the publisher's own stamp
   * (`ask-v2/transport/applyStreamSnapshot.ts::fitCutThisFrame`); this is the
   * second line, for rows that never went through that overlay. Defaults to
   * `false`, so a caller that says nothing gets the conservative hold.
   */
  allowShrink?: boolean;
};

/**
 * Append-only freeze: once the user has seen a segment slot, later frames may
 * update that slot in place or append new slots — never reorder or insert
 * above text the user already read (Path-2 streaming → Path-1 contentBlocks).
 *
 * StabilizedChatMessage still switches to contentBlocks on finalize; this
 * merge is what production chat needs to prevent the "Let me check…" flip.
 */
export function stabilizeDisplaySegments(
  previous: DisplaySegment[],
  next: DisplaySegment[],
  options?: StabilizeDisplaySegmentsOptions,
): DisplaySegment[] {
  if (previous.length === 0) return next;
  const allowShrink = options?.allowShrink ?? false;

  const stabilized: DisplaySegment[] = [];
  // Text slots refreshed via lookahead must not be re-appended. Tools may still
  // rematch the same coalesced Path-1 tools segment into multiple frozen slots.
  const consumedTextIndexes = new Set<number>();
  let nextIndex = 0;

  for (const prevSeg of previous) {
    let matchIndex = -1;
    for (let j = nextIndex; j < next.length; j++) {
      if (consumedTextIndexes.has(j)) continue;
      if (next[j]?.type === prevSeg.type) {
        matchIndex = j;
        break;
      }
    }

    if (matchIndex === nextIndex) {
      stabilized.push(
        mergeSegmentInPlace(
          prevSeg,
          next[matchIndex] as DisplaySegment,
          allowShrink,
        ),
      );
      nextIndex = matchIndex + 1;
      while (consumedTextIndexes.has(nextIndex)) nextIndex += 1;
      continue;
    }

    if (matchIndex > nextIndex) {
      // Keep frozen slot order, but still refresh steps/text from the matching
      // candidate when Path-1 snapshots temporarily reorder segments.
      stabilized.push(
        mergeSegmentInPlace(
          prevSeg,
          next[matchIndex] as DisplaySegment,
          allowShrink,
        ),
      );
      if (prevSeg.type === "text") {
        consumedTextIndexes.add(matchIndex);
      }
      continue;
    }

    stabilized.push(prevSeg);
  }

  for (let j = nextIndex; j < next.length; j++) {
    if (consumedTextIndexes.has(j)) continue;
    const seg = next[j] as DisplaySegment;
    if (seg.type === "tools" && seg.steps.length === 0) continue;
    stabilized.push(seg);
  }

  return stabilized;
}
